import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";

const LoadingDots = () => (
  <span className="inline-flex items-center">
    <span className="animate-bounce mx-0.5 inline-block h-1.5 w-1.5 rounded-full bg-current" style={{ animationDelay: "0ms" }}></span>
    <span className="animate-bounce mx-0.5 inline-block h-1.5 w-1.5 rounded-full bg-current" style={{ animationDelay: "100ms" }}></span>
    <span className="animate-bounce mx-0.5 inline-block h-1.5 w-1.5 rounded-full bg-current" style={{ animationDelay: "200ms" }}></span>
  </span>
);

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  isLoading: boolean;
  iconBgClass: string;
  iconTextClass: string;
  isClickable?: boolean;
  onClick?: () => void;
  brandColor?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const StatCard = ({ 
  icon, 
  label, 
  value, 
  isLoading, 
  iconBgClass, 
  iconTextClass, 
  isClickable = false, 
  onClick,
  brandColor,
  trend 
}: StatCardProps) => {
  const baseClasses = "relative overflow-hidden bg-white p-3 sm:p-4 md:p-6 rounded-xl border flex items-center group";
  const clickableClasses = isClickable ? "cursor-pointer hover:shadow-md transition-all duration-200" : "";
  
  // Dynamic border and background based on brand color
  const borderStyle = brandColor ? { borderColor: `${brandColor}30` } : {};
  const bgGradient = brandColor ? {
    background: `linear-gradient(135deg, ${brandColor}08 0%, white 50%, white 100%)`
  } : {};

  return (
    <div 
      className={`${baseClasses} ${clickableClasses} ${!brandColor ? 'border-[#E5EAF0]' : ''}`} 
      onClick={isClickable ? onClick : undefined}
      style={{ ...borderStyle, ...bgGradient }}
    >
      <div 
        className={`h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 rounded-full ${!brandColor ? iconBgClass : ''} flex items-center justify-center mr-2 sm:mr-3 md:mr-4 flex-shrink-0 relative`}
        style={brandColor ? {
          backgroundColor: `${brandColor}15`,
        } : undefined}
      >
        <div 
          className={`h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 ${!brandColor ? iconTextClass : ''}`}
          style={brandColor ? { color: brandColor } : undefined}
        >
          {icon}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm text-[#64748B] leading-tight">{label}</p>
        <div className="flex items-center gap-2">
          <p className="text-lg sm:text-xl md:text-2xl font-bold text-[#222B45] leading-tight break-words">
            {isLoading ? <LoadingDots /> : value}
          </p>
          {!isLoading && trend && (
            <div className={`flex items-center gap-1 ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {trend.isPositive ? (
                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
              ) : (
                <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4" />
              )}
              <span className="text-xs sm:text-sm font-medium">{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Hover effect overlay */}
      {brandColor && isClickable && (
        <div 
          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${brandColor}10 0%, transparent 100%)`
          }}
        />
      )}
    </div>
  );
};

export default StatCard;
